<template>
  <FadeTransition>
    <template v-if="corporation">
      <CorporationTable :corporation="corporation" />
    </template>
    <div v-else :class="$style.loader">
      <LoadingIndicator />
    </div>
  </FadeTransition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import {
  useSettingsPageCorporationPlannerQuery,
  useSettingsPageCorporationViewQuery,
} from '@/pages/SettingsPage/SettingsPageCorporationView/SettingsPageCorporationView.generated.ts'
import type { Planner, Corporation } from '@/types/app'
import FadeTransition from '@/components/transitions/FadeTransition.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import CorporationTable from './CorporationTable.vue'
import { parseCorporationResponseToCorporation } from '@/apis/repositories/parsers/corporation.ts'
import { parsePlannerProfileResponseToPlanner } from '@/apis/repositories/parsers/planner.ts'

useHead({ title: '企業情報' })

const pagePlannerQuery = useSettingsPageCorporationPlannerQuery()

const planner = computed<Planner | undefined>(() => {
  const data = pagePlannerQuery.result?.value?.planner
  const allSpecialty = pagePlannerQuery.result?.value?.specialties || []
  if (data) {
    return parsePlannerProfileResponseToPlanner(
      {
        ...data,
        id: parseInt(data.id),
        corporationId: parseInt(data.corporationId),
        branchIds: data.branchIds.map(id => parseInt(id)),
        specialties: data.specialties.map(specialty => specialty.name),
      },
      allSpecialty.map(specialty => ({
        ...specialty,
        id: parseInt(specialty.id),
      }))
    )
  }
  return undefined
})

const pageQuery = useSettingsPageCorporationViewQuery(
  () => ({
    id: planner.value ? planner.value.corporationId.toString() : '',
  }),
  () => ({
    enabled: !!planner.value,
  })
)

const corporation = computed<Corporation | null>(() => {
  const data = pageQuery.result?.value?.corporation
  if (data) {
    return parseCorporationResponseToCorporation({
      ...data,
      id: parseInt(data.id),
      branches: data.branches.map(branch => ({
        ...branch,
        id: parseInt(branch.id),
      })),
    })
  }
  return null
})
</script>

<style lang="scss" module>
.loader {
  display: flex;
  height: 560px;
  align-items: center;
  justify-content: center;
}
</style>
